import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', void3:'#0A1260', cyan:'#00FFFF', cyanDim:'rgba(0,255,255,0.10)', cyanBorder:'rgba(0,255,255,0.15)', magma:'#FF5722', magmaDim:'rgba(255,87,34,0.12)', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)', green:'#2A8B7A', greenDim:'rgba(42,139,122,0.12)' };

const PLANS = [
  {
    id: 'free', label: 'Free', price: 0, period: '/mo',
    sessions: 2, seats: 1, desc: 'Try it out with your first two studies.',
    features: ['2 sessions / month','1 user','MCP server access','Themes, findings & quotes','Markdown reports'],
    cta: 'Get started free', ctaStyle: 'ghost',
  },
  {
    id: 'starter', label: 'Starter', price: 29, period: '/mo',
    sessions: 15, seats: 1, desc: 'For solo researchers running regular studies.',
    features: ['15 sessions / month','1 user','Priority processing','PDF & Markdown reports','Semantic search across sessions','Email support'],
    cta: 'Get the Starter plan', ctaStyle: 'primary',
  },
  {
    id: 'growth', label: 'Growth', price: 89, period: '/mo',
    sessions: 50, seats: 3, desc: 'For small teams running continuous discovery.',
    popular: true,
    features: ['50 sessions / month','3 seats included','Team collaboration on projects','Everything in Starter','Slack community access','+$20 / extra seat / mo'],
    cta: 'Get the Growth plan', ctaStyle: 'magma',
  },
  {
    id: 'team', label: 'Team', price: 249, period: '/mo',
    sessions: 150, seats: 10, desc: 'For ResearchOps teams at scale.',
    features: ['150 sessions / month','10 seats included','Custom integrations','Dedicated Slack support','SLA available','+$15 / extra seat / mo'],
    cta: 'Get the Team plan', ctaStyle: 'primary',
  },
];

const FAQ = [
  { q: 'What counts as a session?', a: 'One session = one research artifact (audio, video, or transcript) through the full pipeline. A 90-minute interview is one session. Sessions are pooled across your whole team — they\'re not per seat.' },
  { q: 'Can I go over my monthly quota?', a: 'Yes — session overages are billed at $5 per additional session so your work is never blocked. You can also upgrade your plan at any time.' },
  { q: 'What are seats and how do extra seats work?', a: 'A seat is a collaborator who can access your projects, sessions, and research history. Growth includes 3 seats (+$20/seat/mo for more), Team includes 10 seats (+$15/seat/mo). Free and Starter are single-user.' },
  { q: 'What happens to my data if I cancel?', a: 'Your research data stays accessible for 30 days after cancellation. Export your sessions before then.' },
  { q: 'Do you offer refunds?', a: 'Yes — if you\'re not satisfied within your first 7 days, we\'ll refund your payment in full.' },
  { q: 'Can I use the MCP server on the Free plan?', a: 'Yes. All plans include MCP server access so you can use SenseCritiq directly from Claude Desktop.' },
];

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={s.root}>
      <MarketingNav />

      {/* Header */}
      <div style={s.hero} className="scq-hero">
        <div style={s.heroBg} />
        <div style={s.eyebrow}>Pricing</div>
        <h1 style={s.h1}>Simple, researcher-first pricing.</h1>
        <p style={s.heroSub}>Sessions for what you process. Seats for who collaborates. No hidden costs.</p>
      </div>

      {/* Plan grid */}
      <div style={s.plansWrap}>
        <div style={s.plansGrid} className="scq-grid-4">
          {PLANS.map(plan => (
            <div key={plan.id} className="scq-card" style={{...s.planCard, ...(plan.popular ? s.planCardPopular : {})}}>
              {plan.popular && <div style={s.popularTag}>Most popular</div>}

              <div style={s.planTop}>
                <div style={s.planLabel}>{plan.label}</div>
                <div style={s.planPrice}>
                  ${plan.price}<span style={s.planPeriod}>{plan.period}</span>
                </div>
                <div style={s.planSessions}>
                  {plan.sessions} sessions · {plan.seats === 1 ? '1 user' : `${plan.seats} seats`}
                </div>
                <p style={s.planDesc}>{plan.desc}</p>
              </div>

              <ul style={s.featureList}>
                {plan.features.map(f => (
                  <li key={f} style={s.featureItem}>
                    <span style={s.check}>✓</span>{f}
                  </li>
                ))}
              </ul>

              <Link
                to="/sign-up"
                style={{...s.planCta, ...(plan.ctaStyle === 'magma' ? s.planCtaMagma : plan.ctaStyle === 'ghost' ? s.planCtaGhost : s.planCtaPrimary)}}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison note */}
      <div style={s.note}>
        All plans include the MCP server, source-cited findings, and your full session history.
        <br/>Enterprise plans with SSO and custom data retention available - <a href="mailto:hello@sensecritiq.com" style={s.noteLink}>contact us</a>.
      </div>

      {/* FAQ */}
      <div style={s.faqWrap}>
        <div style={s.faqInner}>
          <h2 style={s.faqHeading}>Frequently asked questions</h2>
          <div style={s.faqList}>
            {FAQ.map((item, i) => (
              <div key={i} style={s.faqItem}>
                <button style={s.faqQ} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <span style={{...s.faqArrow, transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)'}}>↓</span>
                </button>
                {openFaq === i && <div style={s.faqA}>{item.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={s.bottomCta}>
        <div style={s.bottomCtaInner} className="scq-bottom-inner">
          <h2 style={s.bottomH2}>Start with two free sessions.</h2>
          <p style={s.bottomSub}>No credit card required. Upgrade when you're ready.</p>
          <Link to="/sign-up" style={s.bottomBtn}>Create free account</Link>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },
  nav: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 48px', background:'rgba(3,10,60,0.9)', backdropFilter:'blur(16px)', borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:100 },
  logo: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:17, fontWeight:700, letterSpacing:'-0.02em', color:C.white, textDecoration:'none', display:'flex', alignItems:'center', gap:8 },
  navLinks: { display:'flex', gap:28 },
  navLink: { fontSize:14, color:C.muted, textDecoration:'none' },
  navCta: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:13, padding:'10px 20px', borderRadius:8, background:C.magma, color:'#fff', textDecoration:'none' },
  hero: { textAlign:'center', padding:'100px 24px 80px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(36px,5vw,64px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.05, marginBottom:20, position:'relative' },
  heroSub: { fontSize:18, color:C.muted, maxWidth:460, margin:'0 auto', lineHeight:1.65, position:'relative' },
  plansWrap: { maxWidth:1100, margin:'0 auto', padding:'0 24px 60px' },
  plansGrid: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 },
  planCard: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:20, padding:'28px 24px', display:'flex', flexDirection:'column', position:'relative', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  planCardPopular: { border:'1px solid rgba(255,87,34,0.35)', background:'linear-gradient(135deg, rgba(10,18,96,0.72) 0%, rgba(14,22,110,0.75) 100%)' },
  popularTag: { position:'absolute', top:-1, right:20, fontSize:10, fontWeight:700, fontFamily:"'Satoshi','Inter',sans-serif", letterSpacing:'0.07em', textTransform:'uppercase', color:'#030A3C', background:C.magma, padding:'4px 10px', borderRadius:'0 0 8px 8px' },
  planTop: { marginBottom:20 },
  planLabel: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:12, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:C.muted, marginBottom:12 },
  planPrice: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:40, fontWeight:700, letterSpacing:'-0.04em', color:C.white, lineHeight:1, marginBottom:4 },
  planPeriod: { fontSize:14, fontWeight:400, color:C.muted },
  planSessions: { fontSize:13, color:C.cyan, fontWeight:500, marginBottom:10 },
  planDesc: { fontSize:13, color:C.muted, lineHeight:1.5 },
  featureList: { listStyle:'none', padding:0, margin:'0 0 28px', flex:1 },
  featureItem: { fontSize:13, color:C.muted, marginBottom:10, display:'flex', gap:9, lineHeight:1.5 },
  check: { color:C.green, flexShrink:0, fontWeight:700 },
  planCta: { display:'block', textAlign:'center', padding:'12px 0', borderRadius:10, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:14, textDecoration:'none', marginTop:'auto' },
  planCtaMagma: { background:C.magma, color:'#fff' },
  planCtaPrimary: { background:C.cyanDim, color:C.cyan, border:`1px solid ${C.cyanBorder}` },
  planCtaGhost: { background:'rgba(248,249,250,0.05)', color:C.muted, border:`1px solid ${C.border}` },
  note: { textAlign:'center', fontSize:13, color:C.muted2, maxWidth:560, margin:'0 auto 80px', lineHeight:1.7, padding:'0 24px' },
  noteLink: { color:C.cyan, textDecoration:'none' },
  faqWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  faqInner: { maxWidth:680, margin:'0 auto' },
  faqHeading: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:28, fontWeight:700, letterSpacing:'-0.02em', marginBottom:40, textAlign:'center' },
  faqList: { display:'flex', flexDirection:'column', gap:2 },
  faqItem: { borderBottom:`1px solid ${C.border}` },
  faqQ: { width:'100%', textAlign:'left', background:'none', border:'none', padding:'18px 0', fontSize:15, fontWeight:600, fontFamily:"'Satoshi','Inter',sans-serif", color:C.white, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', letterSpacing:'-0.01em' },
  faqArrow: { color:C.cyan, fontSize:14, transition:'transform 0.2s', flexShrink:0 },
  faqA: { fontSize:14, color:C.muted, lineHeight:1.7, paddingBottom:18 },
  bottomCta: { padding:'80px 24px' },
  bottomCtaInner: { maxWidth:1052, margin:'0 auto', background:`linear-gradient(135deg, ${C.void3} 0%, #0A0E4A 100%)`, border:`1px solid ${C.border2}`, borderRadius:24, padding:'70px 48px', textAlign:'center', position:'relative', overflow:'hidden' },
  bottomH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,40px)', fontWeight:700, letterSpacing:'-0.02em', marginBottom:14 },
  bottomSub: { fontSize:16, color:C.muted, marginBottom:36 },
  bottomBtn: { display:'inline-block', padding:'14px 32px', borderRadius:10, background:C.magma, color:'#fff', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none' },
  footer: { borderTop:`1px solid ${C.border}`, padding:'28px 48px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 },
  footerLogo: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:14, color:C.muted, letterSpacing:'-0.01em' },
  footerTagline: { fontSize:12, color:C.muted2 },
  footerLinks: { display:'flex', gap:20 },
  footerLink: { fontSize:12, color:C.muted2, textDecoration:'none' },
};
